import { Component, Injector, Input, OnInit, computed, inject, signal } from '@angular/core';
import { BaseModalComponent } from '@card-software/library';
import { finalize } from 'rxjs';
import { PoReceiptCoverage, PoReceiptCoverageLine, PoReceiptSourceType, PoReceiptSummary } from '../../../../models/po-receipt/po-receipt';
import { ItemMasterProxyService } from '../../../../proxies/item-master.proxy.service';
import { PoReceiptProxyService } from '../../../../proxies/po-receipt.proxy.service';
import { LocaleHelperService } from '../../../../services/locale-helper.service';
import { currencyLabel } from '../../../../utilities/currency.util';

/**
 * WHY a document would pay more than was received (#5662) — opened from the "Exceeds received" warning on
 * the payment request and payment screens.
 *
 * <p>One tab per purchase order, because the check is per PO and a document can draw on several: when only
 * one of three is over, its tab is where the dialog opens. The figures at the top of each tab are the check's
 * own (handed in, not recomputed), and the items and receipts below come from the same receipt ledger in one
 * batch call — so what the dialog shows is always what the warning was based on.</p>
 *
 * <p>Purely informational — it closes with no result.</p>
 */
@Component({
  selector: 'app-received-coverage-modal',
  templateUrl: './received-coverage-modal.component.html',
})
export class ReceivedCoverageModalComponent extends BaseModalComponent<null> implements OnInit {
  private readonly poReceiptService = inject(PoReceiptProxyService);
  private readonly itemMasterService = inject(ItemMasterProxyService);
  private readonly locale = inject(LocaleHelperService);

  /** The check result the screen already holds for this document. */
  @Input({ required: true }) coverage!: PoReceiptCoverage;

  readonly summaries = signal<Map<string, PoReceiptSummary>>(new Map());
  readonly isLoading = signal(true);
  readonly failed = signal(false);
  readonly activeTab = signal('');
  /** Item Master names by code. Empty until the batch returns; the code alone still identifies the item. */
  readonly itemNamesByCode = signal<Map<string, string>>(new Map());

  readonly purchaseOrders = computed<PoReceiptCoverageLine[]>(() => this.coverage?.purchaseOrders ?? []);

  readonly SourceType = PoReceiptSourceType;
  readonly currencyLabel = currencyLabel;

  constructor(injector: Injector) {
    super(injector);
  }

  ngOnInit(): void {
    const lines = this.purchaseOrders();
    // Open on the PO that is the problem, not merely the first one.
    this.activeTab.set((lines.find((l) => l.shortfall > 0) ?? lines[0])?.purchaseOrderReference ?? '');

    const references = lines.filter((l) => l.checked).map((l) => l.purchaseOrderReference);
    if (references.length === 0) {
      this.isLoading.set(false);
      return;
    }

    this.poReceiptService
      .getSummaries(references)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response) => {
          const summaries = response.data ?? [];
          this.summaries.set(new Map(summaries.map((s) => [s.purchaseOrderReference, s] as const)));
          this.loadItemNames(summaries);
        },
        error: () => this.failed.set(true),
      });
  }

  /**
   * Names for every item across the document's POs, in ONE Item API call however many tabs there are —
   * the codes repeat heavily between POs of the same provider.
   */
  private loadItemNames(summaries: PoReceiptSummary[]): void {
    const codes = [...new Set(summaries.flatMap((s) => s.items.map((i) => i.itemCode)).filter((c) => c !== ''))];
    if (codes.length === 0) return;

    this.itemMasterService.getByItemCodes(codes).subscribe({
      next: (response) => {
        const map = new Map<string, string>();
        for (const item of response?.data ?? []) {
          const name = this.locale.getLocalizedValue(item.itemDisplayName);
          if (item.itemCode && name) map.set(item.itemCode, name);
        }
        this.itemNamesByCode.set(map);
      },
      // A name is a nicety; failing quietly keeps the figures on screen.
      error: () => this.itemNamesByCode.set(new Map()),
    });
  }

  itemName(itemCode: string): string {
    return this.itemNamesByCode().get(itemCode) ?? '';
  }

  summaryFor(line: PoReceiptCoverageLine): PoReceiptSummary | undefined {
    return this.summaries().get(line.purchaseOrderReference);
  }

  /** The documents of this request or payment, other than the one the dialog is about. */
  othersInBatch(line: PoReceiptCoverageLine) {
    return line.documents.filter((d) => d.inThisBatch && !d.isThisDocument);
  }

  /** Documents that have already paid something on the PO. */
  paidBefore(line: PoReceiptCoverageLine) {
    return line.documents.filter((d) => d.paidToDate !== 0);
  }

  /** Tab values are the PO references, so anything else is ignored rather than cast. */
  onTabChange(value: string | number | undefined): void {
    if (typeof value === 'string') this.activeTab.set(value);
  }

  onClose(): void {
    this.close({ action: 'Cancel', data: null });
  }
}
